import React, { useEffect, useRef, useState } from 'react'
import Select from 'react-select'

import useMenu from './useMenu'
import DateRangeControl from './DateRangeControl'
import { GRANULARITIES } from './activityReport'

/**
 * One band replacing the three the old page used.
 *
 * The change that matters is the filters: four permanently-open "All ..."
 * dropdowns took most of a row to say nothing. Here a dimension only takes
 * space once it is actually filtering, and "Add filter" offers the rest.
 *
 * An applied filter renders as one flat token -- a label segment and a value
 * segment inside a single bordered pill, `Branch │ Brooklyn +2 │ ×`. The
 * react-select control that actually edits the value is not part of that
 * row: it lives in a popover opened by clicking the value segment, so the
 * toolbar itself never hosts a form control, only the token summarising it.
 */

// The kinds every report offers, regardless of which additional dimension (if
// any) it declares. `roles` options are the fixed `ROLE_OPTIONS` from
// activityReport.js -- but this toolbar stays generic and never imports that
// constant itself; it reads `options.roles` exactly like `options[kind.key]`
// for any other kind, and it's ReportsPanel's job to pass `roles:
// ROLE_OPTIONS` down.
const BASE_KINDS = [
    { key: 'roles', label: 'Role', placeholder: 'Select roles' },
    { key: 'branches', label: 'Branch', placeholder: 'Select branches' },
    { key: 'users', label: 'User', placeholder: 'Select users' },
    { key: 'groups', label: 'Group', placeholder: 'Select groups' },
    { key: 'actions', label: 'Action', placeholder: 'Select actions' },
    { key: 'excludeUsers', label: 'Exclude users', placeholder: 'Select users to exclude' },
]

/**
 * The filter kinds this toolbar can offer, given one report's `scopeLabel`
 * (`ReportSpec.scope_label` -- "Board" for `boards`, "Inbox" for `inbox`,
 * `null`/`undefined` for the twelve reports with no second dimension; see
 * `scopeLabelFor` in reportRegistry.js).
 *
 * This can no longer be a static module list the way it used to be: the one
 * new kind's *label* is per-report (`scopeLabel` itself), not a fixed
 * string, so two reports mounting this toolbar in the same session need two
 * different labels for the same `scope` key. Built fresh from `scopeLabel`
 * on every render instead.
 *
 * The `scope` entry is always present here, whatever `scopeLabel` is --
 * *whether* it is actually offered is `availableKinds`'s call alone (passed
 * down from `ReportsPanel`, gated on the very same `scopeLabel`/
 * `scopeLabelFor`; see its own comment there). Deliberately a single gate
 * rather than two: a second, independent "only if scopeLabel is truthy"
 * check here would make it impossible to tell, from a broken
 * `availableKinds`, whether this toolbar is honouring it -- a bug in that
 * gate would be silently caught by this one instead of surfacing. The
 * fallback label below only matters if that gate is ever broken; a report
 * with no dimension is never supposed to reach it in normal operation.
 *
 * Inserted right after `branches` (the second entry of `BASE_KINDS`, `roles`
 * being the first): the by-branch chart is where a report's "by <dimension>"
 * chart sits too (see `standardChartsFor` in reportRegistry.js), so the two
 * filters read as neighbours in the toolbar for the same reason.
 */
const buildKinds = (scopeLabel) => {
    const label = scopeLabel || 'Scope'
    const scopeKind = { key: 'scope', label, placeholder: `Select ${label.toLowerCase()}s` }
    return [...BASE_KINDS.slice(0, 2), scopeKind, ...BASE_KINDS.slice(2)]
}

/** "Brooklyn +2" -- the full list lives in the open select, not the token. */
const summariseValue = (selected) => {
    if (!selected.length) return null
    const [first, ...rest] = selected
    return { primary: first.label, extra: rest.length ? `+${rest.length}` : null }
}

// Not a JSX style prop (that stays banned) -- react-select takes its own
// styling API as plain objects, so this is the config, declared once outside
// the component rather than re-built every render.
const selectStyles = { menu: (base) => ({ ...base, zIndex: 40 }) }

// Button labels for the Day/Week/Month switch -- `GRANULARITIES` itself
// stays the lowercase values every builder in activityReport.js takes.
const GRANULARITY_LABELS = { day: 'Day', week: 'Week', month: 'Month' }

/**
 * One applied-filter token: the label/value pill plus the popover that holds
 * its react-select control. A component of its own (rather than a chunk of
 * ReportToolbar's render) because it needs its own `useMenu` instance for the
 * popover -- one hook call per token, which only works with one component
 * instance per token.
 *
 * `autoOpen` is set for exactly one render: the one where this token was just
 * revealed by picking its kind from the "Add filter" menu. It is read only by
 * the mount effect below (empty deps, so it only ever sees that first value),
 * which is what makes "choosing a kind moves focus into its control" work
 * without the parent needing to reach into this component's popover state.
 *
 * `isMulti` (default `true`, this toolbar's own filters are all multi-value)
 * and `removable` (default `true`) let a second caller reuse the same token
 * for a single-value filter with no way to clear it independently of picking
 * a value -- the Investigate tab's User/Actions row (InvestigateTab.js) is
 * that second caller, which is why this component is exported. `isMulti`
 * false switches the inner Select to single-select: it takes/returns one
 * option rather than an array, so `onChange` here still normalises back to
 * an array (`value ? [value] : []]`) before handing it to the caller --
 * `summariseValue`, and every caller's own state, keeps treating `values` as
 * a list either way. Single-select also closes the popover on pick, the way
 * a plain dropdown would; the multi-select stays open (`closeMenuOnSelect`
 * false) so more than one value can be picked in one visit.
 */
export const FilterToken = ({ kind, values, options, onChange, onRemove, autoOpen, isMulti = true, removable = true }) => {
    const popover = useMenu()
    const selectRef = useRef(null)
    const isExclusion = kind.key === 'excludeUsers'
    const summary = summariseValue(values)

    useEffect(() => {
        if (autoOpen) popover.open()
        // Only ever meant to run once, for the render this token was created
        // on -- not every time `autoOpen`/`popover` identity happens to change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (popover.isOpen) selectRef.current?.focus()
    }, [popover.isOpen])

    const handleSelectChange = (value) => {
        if (isMulti) {
            onChange(value || [])
        } else {
            onChange(value ? [value] : [])
            popover.close()
        }
    }

    return (
        <div
            className={`reports-filter-token${isExclusion ? ' reports-filter-token--exclude' : ''}`}
            ref={popover.containerRef}
            data-exclusion={isExclusion ? 'true' : undefined}
        >
            <span className="reports-filter-token-label">{kind.label}</span>
            <button
                type="button"
                ref={popover.toggleRef}
                className="reports-filter-token-value"
                aria-haspopup="true"
                aria-expanded={popover.isOpen}
                onClick={() => (popover.isOpen ? popover.close() : popover.open())}
            >
                {summary ? (
                    <>
                        {summary.primary}
                        {summary.extra && (
                            <span className="reports-filter-token-value-extra"> {summary.extra}</span>
                        )}
                    </>
                ) : (
                    kind.placeholder
                )}
            </button>
            {removable && (
                <button
                    type="button"
                    className="reports-filter-token-remove"
                    aria-label={`Remove ${kind.label} filter`}
                    onClick={onRemove}
                >
                    &times;
                </button>
            )}
            {popover.isOpen && (
                <div className="reports-filter-token-popover">
                    <Select
                        ref={selectRef}
                        aria-label={`${kind.label} filter`}
                        classNamePrefix="reports-select"
                        placeholder={kind.placeholder}
                        value={isMulti ? values : (values[0] || null)}
                        onChange={handleSelectChange}
                        options={options}
                        isMulti={isMulti}
                        closeMenuOnSelect={!isMulti}
                        // The popover exists to *be* the picker, so the option
                        // list has to be visible the moment it opens. Focusing
                        // the control is not enough -- react-select opens its
                        // menu on click or keypress, not on focus -- so without
                        // this the popover shows an empty collapsed box and the
                        // filter reads as having no options at all.
                        //
                        // Safe as an uncontrolled default because the Select is
                        // conditionally rendered: it mounts fresh every time the
                        // popover opens, so this applies on every open, not just
                        // the first.
                        defaultMenuIsOpen
                        styles={selectStyles}
                        // Deliberately NOT portalled (no menuPortalTarget): useMenu's
                        // click-outside check is `containerRef.current.contains(event.target)`,
                        // so the select's own dropdown has to stay a DOM descendant of
                        // this token's container -- portalling it to document.body would put
                        // clicks on its options outside that container and close the popover
                        // out from under the click. Nothing between here and `.reports-card`
                        // clips overflow, so the open menu is never cut off by staying in place.
                    />
                </div>
            )}
        </div>
    )
}

const ReportToolbar = ({
    startDate,
    endDate,
    onDateChange,
    filters,
    onFiltersChange,
    options,
    availableKinds,
    scopeLabel,
    groupingOptions,
    grouping,
    onGroupingChange,
    view,
    onViewChange,
    granularity,
    onGranularityChange,
}) => {
    // A dimension is shown once it is applied, or once the user adds it while
    // still empty -- otherwise picking "User" from the menu would open nothing.
    const [revealed, setRevealed] = useState([])
    // The kind whose token should auto-open its popover (and focus its
    // select) on this render -- set for one render by chooseKind, then
    // cleared once FilterToken has had the chance to read it at mount.
    const [justAddedKey, setJustAddedKey] = useState(null)
    const addMenu = useMenu()

    // See `buildKinds` above: this report's own kind list, `scope` included
    // (labelled from `scopeLabel`) only when the report declares one.
    const KINDS = buildKinds(scopeLabel)

    const isShown = (kind) => filters[kind.key]?.length > 0 || revealed.includes(kind.key)

    const shownKinds = KINDS.filter((kind) => availableKinds.includes(kind.key) && isShown(kind))
    const addableKinds = KINDS.filter((kind) => availableKinds.includes(kind.key) && !isShown(kind))
    const addableKindKeys = addableKinds.map((kind) => kind.key)

    const setDimension = (key, value) => onFiltersChange({ ...filters, [key]: value || [] })

    // Removing a token takes its popover out of the DOM. The remove button and
    // the "Add filter" toggle both survive the removal, so focus can move
    // there directly in the same handler -- no effect/race needed, and it
    // never falls through to document.body.
    const removeDimension = (key) => {
        setRevealed((previous) => previous.filter((item) => item !== key))
        setDimension(key, [])
        addMenu.toggleRef.current?.focus()
    }

    // Focus the first menu item when the menu opens -- the standard menu-button
    // pattern, and the only way a keyboard user reaches the items without
    // first tabbing past everything else in the toolbar.
    useEffect(() => {
        if (!addMenu.isOpen) return
        addMenu.focusItemAt(addableKindKeys, 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addMenu.isOpen])

    // Clears the one-render `justAddedKey` signal once it's been set -- the
    // newly-mounted FilterToken already captured it (child effects run before
    // a parent's own effect in the same commit), so this just stops it from
    // lingering and re-triggering anything on an unrelated later render.
    useEffect(() => {
        if (justAddedKey === null) return
        setJustAddedKey(null)
    }, [justAddedKey])

    const chooseKind = (key) => {
        setJustAddedKey(key)
        setRevealed((previous) => [...previous, key])
        addMenu.close({ restoreFocus: false })
    }

    return (
        <div className="reports-toolbar">
            <DateRangeControl id="reportsDateRange" startDate={startDate} endDate={endDate} onDateChange={onDateChange} />

            {shownKinds.map((kind) => (
                <FilterToken
                    key={kind.key}
                    kind={kind}
                    values={filters[kind.key] || []}
                    options={options[kind.key]}
                    onChange={(value) => setDimension(kind.key, value)}
                    onRemove={() => removeDimension(kind.key)}
                    autoOpen={kind.key === justAddedKey}
                />
            ))}

            <div className="reports-add-filter-container" ref={addMenu.containerRef}>
                <button
                    ref={addMenu.toggleRef}
                    type="button"
                    className="reports-add-filter"
                    aria-haspopup="menu"
                    aria-expanded={addMenu.isOpen}
                    aria-controls={addMenu.isOpen ? addMenu.menuId : undefined}
                    onClick={() => (addMenu.isOpen ? addMenu.close() : addMenu.open())}
                >
                    <i className="fa fa-plus" aria-hidden="true"></i> Add filter
                </button>
                {addMenu.isOpen && (
                    <div className="reports-add-filter-menu" role="menu" id={addMenu.menuId}>
                        {addableKinds.length === 0 && (
                            <p className="reports-add-filter-empty">All filters applied</p>
                        )}
                        {addableKinds.map((kind) => (
                            <button
                                type="button"
                                role="menuitem"
                                key={kind.key}
                                ref={addMenu.registerItem(kind.key)}
                                className="reports-add-filter-item"
                                onClick={() => chooseKind(kind.key)}
                                onKeyDown={addMenu.onItemKeyDown(addableKindKeys, kind.key)}
                            >
                                {kind.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="reports-toolbar-toggles">
                <div className="reports-grouping-toggle" role="group" aria-label="Group by">
                    {groupingOptions.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            aria-pressed={grouping === option.id}
                            className={grouping === option.id ? 'is-active' : ''}
                            onClick={() => onGroupingChange(option.id)}
                        >
                            {option.name}
                        </button>
                    ))}
                </div>

                <div className="reports-granularity-toggle" role="group" aria-label="Time bucket">
                    {GRANULARITIES.map((g) => (
                        <button
                            key={g}
                            type="button"
                            aria-pressed={granularity === g}
                            className={granularity === g ? 'is-active' : ''}
                            onClick={() => onGranularityChange(g)}
                        >
                            {GRANULARITY_LABELS[g]}
                        </button>
                    ))}
                </div>

                <div className="reports-view-toggle">
                    <button
                        type="button"
                        aria-pressed={view === 'chart'}
                        className={view === 'chart' ? 'is-active' : ''}
                        onClick={() => onViewChange('chart')}
                    >
                        <i className="fa fa-bar-chart" aria-hidden="true"></i> Chart
                    </button>
                    <button
                        type="button"
                        aria-pressed={view === 'table'}
                        className={view === 'table' ? 'is-active' : ''}
                        onClick={() => onViewChange('table')}
                    >
                        <i className="fa fa-table" aria-hidden="true"></i> Table
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ReportToolbar
